import { Link } from 'features/i18n/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { Thread } from 'lib/_types'
import { appendList } from 'lib/helpers'
import { useInfinityScrollSimple } from 'hooks'
import { ROUTE_PATH } from 'lib/paths'
import { useGroupUserRole } from 'pages/Group/hooks/useGroupUserRole'
import {
  useAddPostToThreadMutation,
  useGetThreadsQuery,
} from 'store/api/yay/thread'

import ImageLoader from 'components/ImageLoader'
import LoadMore from 'components/LoadMore'
import Loading from 'components/Loading'
import Button from 'components/Button'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'

import './style.scss'

const ExistingThreadsModal = ({}) => {
  const { t } = useTranslation()
  const { groupId } = useParams()

  const groupUserRole = useGroupUserRole({ groupId })

  return (
    <div className="Modal__wrapper Modal__wrapper--auto-h">
      <div className="Modal__header">
        <Link className="Modal__header__close" to={location.pathname} />

        <h2 className="Modal__header__h2">
          {t('confirm_box_move_post_to_thread.buttons.add_to_existing')}
        </h2>
      </div>

      <div className="Modal__body">
        <div className="ExistingThreadsModal">
          <ThreadList joinStatus="joined" />

          {(groupUserRole == 'moderator' || groupUserRole == 'owner') && (
            <ThreadList joinStatus="unjoined" />
          )}
        </div>
      </div>
    </div>
  )
}

export default ExistingThreadsModal

const ThreadList = ({ joinStatus }: { joinStatus: 'joined' | 'unjoined' }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { groupId } = useParams()
  const [searchParams] = useSearchParams()

  const groupUserRole = useGroupUserRole({ groupId })

  const [__fromId, __fromIdSet] = useState<string>('0')
  const [__threads, __threadsSet] = useState<Array<Thread>>([])
  const [__isLoading, __isLoadingSet] = useState(false)

  const [__confirmBoxData, __confirmBoxDataSet] = useState<{
    isActive: boolean
    postId?: string
    thread?: Thread
  }>({
    isActive: false,
  })

  const threadsRes = useGetThreadsQuery({
    groupId,
    joinStatus,
    from: __fromId,
  })

  const [addPostToThread] = useAddPostToThreadMutation()

  const loadMore = () => {
    __fromIdSet(threadsRes?.data?.next_page_value?.toString?.())
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore: loadMore,
  })

  useEffect(() => {
    if (threadsRes?.isSuccess && threadsRes?.data) {
      __threadsSet((prevState) =>
        appendList(prevState, threadsRes?.data?.threads, 'updated_at')
      )
    }
  }, [threadsRes])

  if (
    joinStatus == 'joined' &&
    (groupUserRole == 'owner' || groupUserRole == 'moderator') &&
    (threadsRes?.isLoading ||
      (!threadsRes?.isLoading && __threads?.length == 0))
  ) {
    return null
  }

  // https://jira.nanameue.jp/browse/YAY-13405
  if (
    joinStatus == 'unjoined' &&
    threadsRes?.isSuccess &&
    __threads?.length == 0
  ) {
    return null
  }

  return (
    <div className="ExistingThreadsModal__thread-list">
      <h3 className="ExistingThreadsModal__thread-list__h3">
        {joinStatus == 'joined'
          ? t('group_threads_page.box_header.joined_thread')
          : t('group_threads_page.box_header.unjoined_thread')}
      </h3>

      {threadsRes?.isLoading && <Loading />}

      {!threadsRes?.isLoading && __threads?.length == 0 && (
        <div className="Placeholder">
          <h6 className="Placeholder__h6">
            {t('add_post_to_existing_thread.placeholder.title')}
          </h6>

          {joinStatus == 'joined' && (
            <p className="Placeholder__p">
              {t('add_post_to_existing_thread.placeholder.description')}
            </p>
          )}
        </div>
      )}

      {__threads?.length > 0 && (
        <>
          <dl className="ExistingThreadsModal__thread-list__dl">
            {__threads?.map?.((x, i) => (
              <div
                className="ExistingThreadsModal__thread-list__dl__item"
                key={i}
              >
                <ImageLoader
                  url={x?.thread_icon ?? x?.owner?.profile_icon_thumbnail}
                  className="ExistingThreadsModal__thread-list__dl__item__avatar"
                />

                <dt className="ExistingThreadsModal__thread-list__dl__item__dt">
                  {x?.title}
                </dt>

                <Button
                  buttonStyle="bordered-primary-without-bg"
                  size="small"
                  onClick={() => {
                    __confirmBoxDataSet({
                      isActive: true,
                      thread: x,
                    })
                  }}
                >
                  {t('common.add')}
                </Button>
              </div>
            ))}
          </dl>

          <LoadMore
            isLoading={threadsRes?.isFetching}
            hidden={isNil(threadsRes?.data?.next_page_value)}
            onClick={() => {
              loadMore()
            }}
            ref={loadMoreRef}
          />
        </>
      )}

      {(() => {
        const onClose = () => {
          __confirmBoxDataSet((prevState) => {
            return {
              ...prevState,
              isActive: false,
            }
          })
        }

        return (
          <ConfirmBoxBasic
            isActive={__confirmBoxData?.isActive}
            onClose={onClose}
            onOk={() => {
              __isLoadingSet(true)

              addPostToThread({
                postId: searchParams.get('postId'),
                threadId: __confirmBoxData?.thread?.id,
              }).then((res) => {
                if (res?.['data']?.id) {
                  // Moved post in the created thread may not appear immediately,
                  // so need to wait a moment.
                  window.setTimeout(() => {
                    navigate(
                      ROUTE_PATH.GROUP_THREAD(
                        groupId,
                        __confirmBoxData?.thread?.id
                      )
                    )
                  }, 1000)
                }
              })
            }}
            onCancel={onClose}
            title={t('add_post_to_existing_thread.confirm.title', {
              name: __confirmBoxData?.thread?.title,
            })}
            loading={__isLoading}
          />
        )
      })()}
    </div>
  )
}
